import { useCallback, useEffect, useRef, useState } from "react";
import { apiCall } from "../../../_services/apiCall";
import config from "../../../config/config";
import {
  listDueDates,
  listFrameworks,
  listQuantitativeKpis,
} from "./dueDatesApi";

/**
 * Reads a JSON value from localStorage without throwing on bad data.
 * @param {string} key the storage key.
 * @returns {*} the parsed value, or null when absent or unparseable.
 */
function safeParseJSON(key) {
  try {
    return JSON.parse(localStorage.getItem(key));
  } catch {
    return null;
  }
}

/**
 * Readable name for a location.
 *
 * `getSource` returns `location` as an object of address parts, not a string —
 * rendering it directly crashes React — so prefer the unit code and fall back to
 * composing the address.
 * @param {object} loc the location record.
 * @returns {string} a displayable label.
 */
export function locationLabel(loc) {
  if (!loc) return "";
  if (loc.unitCode) return loc.unitCode;
  const parts = [loc.location?.area, loc.location?.city].filter(Boolean);
  return parts.length ? parts.join(", ") : `Location ${loc.id}`;
}

/**
 * Today's date as `YYYY-MM-DD` (local), for use as the `min` of a due-date
 * input. A due date can't be in the past, so the picker shouldn't offer it.
 * @returns {string} the local calendar date, zero-padded.
 */
export function todayISO() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

/**
 * Formats a stored due date for display. Single source of truth so every place
 * that shows a deadline reads it the same way.
 * @param {string|null} value the date as `YYYY-MM-DD`.
 * @returns {string} a readable date, or an em dash when unset.
 */
export function formatDate(value) {
  if (!value) return "—";
  return new Date(`${value}T00:00:00Z`).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

/**
 * Maps each KPI to the locations it is assigned to.
 *
 * A location deadline covers the KPIs assigned to that location, so the screen
 * needs the assignment to know which rows a location scope actually reaches.
 * @param {object[]} assignedDetail the assignment records from the API.
 * @returns {Map<number, Set<number>>} KPI id to its assigned location ids.
 */
function indexAssignments(assignedDetail) {
  const byKpi = new Map();
  for (const detail of assignedDetail ?? []) {
    const kpiId = Number(detail.questionId);
    const locations = byKpi.get(kpiId) ?? new Set();
    for (const sourceId of detail.sourceIds ?? []) locations.add(Number(sourceId));
    byKpi.set(kpiId, locations);
  }
  return byKpi;
}

/**
 * Reshapes reporting questions into the row shape this screen renders.
 *
 * The reporting endpoint calls the identifier `questionId`; the rest of this
 * feature calls it `kpiId`, so the rename happens once here rather than in
 * every consumer.
 * @param {object[]} questions the reporting questions.
 * @returns {object[]} KPI rows.
 */
function normaliseKpis(questions) {
  return questions.map((q) => ({
    kpiId: q.questionId,
    title: q.title,
    tags: q.tags,
    moduleName: q.moduleName ?? null,
  }));
}

/** Scope a due date can be set at, mirroring the backend enum. */
export const SCOPE = { MODULE: "MODULE", LOCATION: "LOCATION", KPI: "KPI" };

/**
 * Loads everything the Set Due Date screen renders from: the financial year's
 * KPIs, the company's locations, and the deadlines already configured.
 *
 * Reads are race-guarded with a fetch id, so switching financial year quickly
 * can't let a slower earlier response overwrite the newer one.
 * @param {object} selectedFY the chosen financial year.
 * @returns {object} kpis, locations, saved due dates, loading/error and refresh.
 */
export function useDueDates(selectedFY) {
  const [kpis, setKpis] = useState([]);
  const [assignments, setAssignments] = useState(() => new Map());
  const [locations, setLocations] = useState([]);
  const [dueDates, setDueDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetchId = useRef(0);

  const load = useCallback(async () => {
    if (!selectedFY?.id) return;
    const id = ++fetchId.current;
    setLoading(true);
    setError(null);

    try {
      const fwRes = await listFrameworks(safeParseJSON("user_temp_id"));
      if (id !== fetchId.current) return;
      const frameworkIds = (fwRes?.isSuccess ? fwRes.data?.data ?? [] : []).map(
        (fw) => fw.id,
      );

      const [kpiRes, sourceRes, dueRes] = await Promise.all([
        listQuantitativeKpis(selectedFY.id, frameworkIds),
        apiCall(`${config.POSTLOGIN_API_URL_COMPANY}getSource`, {}, {}, "GET"),
        listDueDates(selectedFY.id),
      ]);
      if (id !== fetchId.current) return;
      setLoading(false);

      if (kpiRes?.isSuccess) {
        setKpis(normaliseKpis(kpiRes.data?.data ?? []));
        setAssignments(indexAssignments(kpiRes.data?.assignedDetail));
      } else setError("Could not load KPIs");

      if (sourceRes?.isSuccess) setLocations(sourceRes.data?.data ?? []);
      if (dueRes?.isSuccess) setDueDates(dueRes.data ?? []);
    } catch {
      if (id !== fetchId.current) return;
      setLoading(false);
      setError("Could not load due dates");
    }
  }, [selectedFY]);

  useEffect(() => {
    load();
    /** Bumping the fetch id on unmount makes any in-flight response a no-op. */
    return () => {
      fetchId.current += 1;
    };
  }, [load]);

  return { kpis, assignments, locations, dueDates, loading, error, refresh: load };
}

/**
 * Indexes saved due dates so a row can find its own without rescanning.
 * @param {object[]} dueDates the saved due dates.
 * @returns {{byKpi: Map, byLocation: Map, byModule: Map}} lookups keyed by scope.
 */
export function indexDueDates(dueDates) {
  const byKpi = new Map();
  const byLocation = new Map();
  const byModule = new Map();

  for (const row of dueDates ?? []) {
    if (row.scopeType === SCOPE.KPI) byKpi.set(Number(row.questionId), row);
    else if (row.scopeType === SCOPE.LOCATION)
      byLocation.set(Number(row.locationId), row);
    else if (row.scopeType === SCOPE.MODULE) byModule.set(row.module, row);
  }
  return { byKpi, byLocation, byModule };
}

/**
 * Resolves the deadline a KPI currently shows, narrowest scope first — the same
 * precedence the server applies, so the screen never disagrees with the lock.
 *
 * More than one location can be in view at once, and a KPI shared across them
 * can inherit a different location deadline from each; the most urgent (earliest)
 * applicable one wins for display, matching how a lock would bite first.
 * @param {object} kpi the KPI row.
 * @param {number[]} locationIds the in-view locations this KPI belongs to, if any.
 * @param {object} index the lookups from {@link indexDueDates}.
 * @returns {{dueDate: string, source: string}|null} the date and where it came from.
 */
export function resolveRowDueDate(kpi, locationIds, index) {
  const own = index.byKpi.get(Number(kpi.kpiId));
  if (own) return { dueDate: own.dueDate, source: "KPI" };

  const locationDate = (locationIds ?? [])
    .map((id) => index.byLocation.get(Number(id))?.dueDate)
    .filter(Boolean)
    .sort()
    .shift();
  if (locationDate) return { dueDate: locationDate, source: "Location" };

  const byMod = kpi.moduleName ? index.byModule.get(kpi.moduleName) : null;
  if (byMod) return { dueDate: byMod.dueDate, source: "Module" };

  return null;
}
